/**
 * useIdleTimer — "nobody has touched the screen for N seconds".
 *
 * WHAT: `{ isIdle, reset }`. One window timeout is armed when the hook
 * is enabled and re-armed on every pointerdown / keydown / touchstart
 * (passive listeners, so scrolling is never blocked); it fires `isIdle`
 * after `idleSeconds` of silence. `reset()` clears the idle flag and
 * re-arms — the kiosk calls it when the idle carousel is dismissed.
 *
 * WHY no state write per touch: a busy kiosk gets hundreds of touches;
 * the activity handler only restarts the timeout and writes state when
 * the flag actually flips, so typing on the sheet never re-renders the
 * page through this hook.
 *
 * The idle flag is remembered with the arm generation it fired for, so
 * a stale "idle" from before the hook was disabled (or reset) can never
 * leak into the next enabled period without the timer running again.
 */

import { useCallback, useEffect, useState } from 'react';

export interface UseIdleTimerOptions {
    /** Seconds of no interaction before `isIdle` turns true. */
    idleSeconds: number;
    /** When false nothing is armed and `isIdle` is always false. */
    enabled?: boolean;
}

export interface IdleTimer {
    isIdle: boolean;
    /** Clear the idle flag and start the countdown again. */
    reset: () => void;
}

const MS_PER_SECOND = 1_000;
const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'touchstart'] as const;
const PASSIVE_LISTENER: AddEventListenerOptions = { passive: true };

export function useIdleTimer({ idleSeconds, enabled = true }: UseIdleTimerOptions): IdleTimer {
    // Every reset (or enable) starts a new generation; the timer records
    // which generation it fired for, so only the current one counts.
    const [generation, setGeneration] = useState(0);
    const [idleGeneration, setIdleGeneration] = useState<number | null>(null);
    // Re-enabling starts a fresh generation (derived during render — the
    // React "adjust state when a prop changes" pattern, no effect write).
    const [previousEnabled, setPreviousEnabled] = useState(enabled);
    if (previousEnabled !== enabled) {
        setPreviousEnabled(enabled);
        setGeneration((current) => current + 1);
    }

    const reset = useCallback(() => {
        setIdleGeneration(null);
        setGeneration((current) => current + 1);
    }, []);

    useEffect(() => {
        if (!enabled) return undefined;
        const idleMs = Math.max(1, idleSeconds) * MS_PER_SECOND;
        let timer: number | undefined;

        const arm = () => {
            window.clearTimeout(timer);
            timer = window.setTimeout(() => setIdleGeneration(generation), idleMs);
        };
        const onActivity = () => {
            // Same-value writes bail out in React, so this is free while awake.
            setIdleGeneration(null);
            arm();
        };

        arm();
        ACTIVITY_EVENTS.forEach((type) => window.addEventListener(type, onActivity, PASSIVE_LISTENER));
        return () => {
            window.clearTimeout(timer);
            ACTIVITY_EVENTS.forEach((type) => window.removeEventListener(type, onActivity));
        };
    }, [enabled, idleSeconds, generation]);

    return {
        isIdle: enabled && idleGeneration === generation,
        reset,
    };
}

export default useIdleTimer;
